import React, {useState, useCallback, useEffect} from 'react';
import {toast} from "react-toastify";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import DeleteSharpIcon from "@mui/icons-material/DeleteSharp";
import {useUserContext} from "../../../contexts/UserContext";
import useDidMountEffect from "../../../hooks/useDidMountEffect";
import {useDebounce} from "@uidotdev/usehooks";
import {buildCarString, buildTrimAndStyleDescriptionName, formatDate} from "../../../utils/utils";
import {ClientItemProfitPopover} from "./ClientItemProfitPopover";
import Badge from "@mui/material/Badge";
import classnames from 'classnames';
import DatePicker from "react-datepicker";
import moment from "moment";
import Stack from "@mui/material/Stack";
import {DealModal} from "../../PricingListPage/DealModal";
import {CRMListDealsModal} from "../CRMListDealsPage/CRMListDealsModal";
import {CustomCRMItemRowEntry} from "../CustomCRMItemRowEntry";
import {useFetchHook} from "../../../hooks/useFetchHook";
import fetcher from "../../../fetcher";
import {useClientsStore} from "../../../stores/clientsStore";
import {useCRMStore} from "../../../stores/crmStore";
import {useModalsStore} from "../../../stores/modalsStore";
import {useShallow} from "zustand/react/shallow";


const getInitialState = (client) => ({...client})

const funcMap = {
    Car: (column, clientState) => {
        if (clientState.column_entries[column]) {
            return clientState.column_entries[column]
        }
        const car_detail = clientState.car_detail
        if (car_detail) {
            const description = buildTrimAndStyleDescriptionName(car_detail.trim, car_detail.style_description)
            return `${car_detail?.year} ${car_detail.make} ${car_detail.model} ${description}`
        } else {
            return ''
        }

    }
}

export const ClientItem = ( { client } ) => {
    const { profileState } = useUserContext()
    const [updateClientResponse, updateClient] = useFetchHook(fetcher.updateClient)
    const [deleteClientResponse, deleteClient] = useFetchHook(fetcher.deleteClient)
    const [clientState, setClientState] = useState(getInitialState(client))
    const debouncedClientState = useDebounce(clientState, 300)

    const {
        openModal,
        findModalById,
        updateModalById,
    } = useModalsStore(
        useShallow((state) => ({
            openModal: state.openModal,
            findModalById: state.findModalById,
            updateModalById: state.updateModalById,
        }))
    )

    const [isFocused, setIsFocused] = useState(false);
    const [isEmailFocused, setIsEmailFocused] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const removeDeletedClientFromList = useClientsStore((state) => state.removeDeletedClientFromList)
    const setIsLoading = useCRMStore((state) => state.setIsLoading)

    useEffect(function updateClientListAfterDeleteEffect() {
        if (deleteClientResponse?.data?.data?.id) {
            removeDeletedClientFromList(deleteClientResponse?.data?.data?.id)
        }
    }, [deleteClientResponse])

    const handleOnFocus = useCallback(() => {
        setIsFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
        setIsFocused(false);
    }, []);

    const handleEmailFocus = () => {
        setIsEmailFocused(true);
    }

    const handleEmailBlur = () => {
        setIsEmailFocused(false);
    }


    const handleActionClick = (event) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget);
    };

    const handleActionClose = () => {
        setAnchorEl(null);
    };

    const updateClientItem = () => {
        setIsLoading(true)
        return updateClient(client.id, clientState).then((data) => {
            setIsLoading(false)
        })
    }

    const deleteClientItem = () => {
        return deleteClient(client.id).then((data) => {
            toast.success("Successfully deleted client.")
        })
    }

    const setStateWithUpdate = (newState) => {
        setClientState({...newState, _should_update: true})
    }

    const setStateWithoutUpdate = (newState) => {
        setClientState({...newState, _should_update: false})
    }

    const setClientQuotes = (toggledDealId) => {
        // right now we are only supporting one Deal per client, but this may change
        let newDealIds = structuredClone(clientState.deals)
        if (newDealIds.includes(toggledDealId)) {
            newDealIds = []
        } else {
            newDealIds = [toggledDealId]
        }

        const newState = {...clientState, deals: newDealIds}
        return updateClient(client.id, newState).then((data) => {
            const newClientData = data?.data
            setStateWithoutUpdate({...newClientData})
            setIsLoading(false)
            return newClientData.deals
        })
    }

    const getMaturityDate = (term, newInceptionDate) => {
        const inceptionDate = moment(newInceptionDate)
        const maturity_date = inceptionDate.clone().add(Number(term), 'M')
        return maturity_date.format("MM/DD/yyyy")
    }

    useDidMountEffect(function updateClientItemAfterDebounce(){
        if (debouncedClientState._should_update === true && !isEmailFocused) {
            updateClientItem()
        }
    }, [debouncedClientState, isEmailFocused])

    const getColumnValue = (columnName) => {
        const func = funcMap[columnName]
        if (!func) {
            return clientState.column_entries[columnName]
        } else {
            return func(columnName, clientState)
        }

    }

    useDidMountEffect(function setClientItemStateOnUpdateResponse(){
        if (updateClientResponse?.data?.data && !updateClientResponse.loading && !updateClientResponse.error) {
            const newClientData = updateClientResponse?.data?.data
            if (newClientData.id === client.id) {
                const newDeals = newClientData.deals
                setStateWithoutUpdate({...clientState, deals: newDeals})

                const modalId = `CRMListDealsModal-${client.id}`
                if (findModalById(modalId)) {
                    updateModalById(modalId, {
                        element: CRMListDealsModal,
                        id: modalId,
                        props: {
                            initialSelectedIds: newClientData.deals,
                            onSelect: (dealId) => setClientQuotes(dealId)
                        }
                    })
                }
            }
        }

    }, [updateClientResponse])

    if (!clientState) return null;

    const columns = profileState.client_columns
    const sortedColumns = Object.keys(columns).sort((a, b) => columns[a] - columns[b])

    return (
        <tr className={'ClientItem'}>
            <td className={'ClientItem-td'} key='Name'>
                <input
                    onFocus={handleOnFocus}
                    onBlur={handleBlur}
                    type="text"
                    onChange={(e) => {
                        setStateWithUpdate({...clientState, name: e.target.value})
                    }}
                    value={clientState.name}
                />
            </td>
            <td className={'ClientItem-td'} key='Phone'>
                <input
                    onFocus={handleOnFocus}
                    onBlur={handleBlur}
                    type="text"
                    onChange={(e) => {
                        setStateWithUpdate({...clientState, phone_number: e.target.value})
                    }}
                    value={clientState.phone_number}
                />
            </td>
            <td className={'ClientItem-td'} key='Email'>
                <input
                    onFocus={() => {
                        handleOnFocus()
                        handleEmailFocus()
                    }}
                    onBlur={() => {
                        handleBlur()
                        handleEmailBlur()
                    }}
                    type="text"
                    onChange={(e) => {
                        setStateWithUpdate({...clientState, email: e.target.value})
                    }}
                    value={clientState.email}
                />
            </td>
            <td
                className={'ClientItem-td deal'}
            >
                {
                    Boolean(clientState.deals.length) &&
                    <Stack
                        direction="row"
                        alignItems="end"
                        justifyContent={"space-between"}
                    >
                        <span
                            className={'ClientItem-td_deal vehicle'}
                            onClick={() => {
                                openModal({
                                    id: "DealModal",
                                    element: DealModal,
                                    props: {id: clientState.deals}
                                })
                            }}>
                            {buildCarString(clientState?.car_detail)}
                        </span>
                        <span>{" "} | {" "}</span>
                        <span
                            className={'ClientItem-td_deal'}
                            onClick={() => {
                                openModal({
                                    element: CRMListDealsModal,
                                    id: "CRMListDealsModal",
                                    props:{
                                        initialSearchMoreOpen: true,
                                        onlyOneSelection: true,
                                        initialSelectedIds: clientState.deals,
                                        onSelect: (dealId) => setClientQuotes(dealId),
                                    }
                                })
                            }}
                        >
                            Edit
                        </span>
                    </Stack>
                }
                {
                    Boolean(!clientState.deals.length) &&
                    <span
                        onClick={() => {
                            openModal({
                                element: CRMListDealsModal,
                                id: "CRMListDealsModal",
                                props: {
                                    onlyOneSelection: true,
                                    initialSelectedIds: clientState.deals,
                                    onSelect: (dealId) => setClientQuotes(dealId),
                                    onDealSelect: (selectedDeal) => {
                                        const dealId = selectedDeal.id
                                        const newDeals = [dealId]
                                        if (clientState.inception_date) {
                                            setStateWithUpdate({
                                                ...clientState,
                                                deal: dealId,
                                                deals: newDeals,
                                                maturity_date: getMaturityDate(selectedDeal.term, clientState.inception_date),
                                                broker_fee_profit: selectedDeal.profit,
                                            })
                                        } else {
                                            setStateWithUpdate({
                                                ...clientState,
                                                deal: dealId,
                                                deals: newDeals,
                                                broker_fee_profit: selectedDeal.profit,
                                            })
                                        }

                                    },
                                }
                            })
                        }}
                        className={'ClientItem-td_deal'}
                    >
                        {clientState.column_entries['Car'] ? clientState.column_entries['Car'] : 'Add Vehicle'}
                    </span>
                }
            </td>
            <td
                className={classnames('ClientItem-td profit', {
                    isPaid: clientState.is_paid,
                })}
                key='Profit'
            >
                <Badge
                    badgeContent={clientState.number_of_profits}
                    sx={{ "& .MuiBadge-badge": { fontSize: 9, height: 15, minWidth: 15 } }}

                >
                    <ClientItemProfitPopover
                        clientState={clientState}
                        setClientState={setStateWithUpdate}
                        handleOnFocus={handleOnFocus}
                        handleBlur={handleBlur}
                    />
                </Badge>
            </td>
            <td className={'ClientItem-td'} key={'Inception Date'}>
                <DatePicker
                    popperClassName={"ClientItem-datepicker"}
                    onFocus={handleOnFocus}
                    onBlur={handleBlur}
                    dateFormat="MM/dd/yyyy"
                    className={'CustomReactDatePicker'}
                    selected={clientState.inception_date}
                    onChange={(date) => {
                        setStateWithUpdate({...clientState, inception_date: formatDate(date)})
                    }}
                    onChangeRaw={(e) => {
                        const newRaw = new Date(e.currentTarget.value);
                        if (newRaw instanceof Date && !isNaN(newRaw)) {
                            setStateWithUpdate({...clientState, inception_date: formatDate(newRaw)})
                        }
                    }}
                />
            </td>
            <td className={'ClientItem-td'} key={'Maturity Date'}>
                <DatePicker
                    popperClassName={"ClientItem-datepicker"}
                    onFocus={handleOnFocus}
                    onBlur={handleBlur}
                    dateFormat="MM/dd/yyyy"
                    className={'CustomReactDatePicker'}
                    selected={clientState.maturity_date || clientState.deal_maturity_date}
                    onSelect={(date) => setStateWithUpdate({...clientState, maturity_date: formatDate(date)})}
                    onChange={(date) => {
                        setStateWithUpdate({...clientState, maturity_date: formatDate(date)})
                    }}
                    onChangeRaw={(e) => {
                        const newRaw = new Date(e.currentTarget.value);
                        if (newRaw instanceof Date && !isNaN(newRaw)) {
                            setStateWithUpdate({...clientState, maturity_date: formatDate(newRaw)})
                        }
                    }}
                />
            </td>
            {
                sortedColumns.map(columnID => {
                    return (
                        <CustomCRMItemRowEntry
                            key={columnID}
                            handleBlur={handleBlur}
                            handleOnFocus={handleOnFocus}
                            columnID={columnID}
                            columns={columns}
                            state={clientState}
                            setStateWithUpdate={setStateWithUpdate}
                        />
                    )
                })
            }
            <td className={'ClientItem-td'}>
                <MoreHorizIcon
                    onClick={handleActionClick}
                    className={'CRM-action-button'}
                />
                <Popover
                    onClick={(e) => e.stopPropagation()}
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={handleActionClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        horizontal: 'right',
                        vertical: 'top'
                    }}
                >
                    <Typography
                        sx={{ p: 2, fontWeight: 500 }}
                        className={'CRM__action-item'}
                    >
                        <span style={{marginRight: "10px"}}>Rollover: </span>
                        <DatePicker
                            popperClassName={"ClientItem-datepicker"}
                            // onFocus={handleOnFocus}
                            // onBlur={handleBlur}
                            inline
                            dateFormat="MM/dd/yyyy"
                            className={'CustomReactDatePicker'}
                            selected={clientState.month_added}
                            onSelect={(date) => setStateWithUpdate({...clientState, month_added: formatDate(date)})}
                            onChange={(date) => {
                                setStateWithUpdate({...clientState, month_added: formatDate(date)})
                            }}
                            onChangeRaw={(e) => {
                                const newRaw = new Date(e.currentTarget.value);
                                if (newRaw instanceof Date && !isNaN(newRaw)) {
                                    setStateWithUpdate({...clientState, month_added: formatDate(newRaw)})
                                }
                            }}
                        />
                    </Typography>
                    <Typography
                        sx={{ p: 2, fontWeight: 500 }}
                        className={'CRM__action-item'}
                        onClick={deleteClientItem}
                    >
                        <DeleteSharpIcon/>
                        Delete Client
                    </Typography>
                </Popover>
            </td>
        </tr>
    )
}